import React, { useReducer, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Grid } from '@mui/material';

import { useViewDocuments } from 'shared/hooks';
import { Loader, Title } from 'ui';
import {
  SummaryCard,
  SummaryCardBusinessCard,
  SummaryCardLineChart,
} from 'modules/common/SummaryCard';
import { ScoreWidgetMainColumn } from 'ui/ScoreWidget';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { creditRatingStatuses } from 'shared/constants/creditRatingStatuses';
import { fetchCreditRatingSummaryTab, fetchEntityDocuments } from 'services';
import { summaryCardMapCreditRatingParser, documentsParser } from 'shared/parsers';
import { getScoreWidgetFooterColor } from 'shared/functions';
import { getEntityShouldRefresh } from 'selectors/entity';
import { useStyles, useHeadSquareStyles } from 'shared/styles/summaryCard';
import {
  actions,
  setIsLoading,
  initialLoadingState,
  isLoadingReducer,
  setData,
  initialDataState,
  dataReducer,
  setStatus,
  initialStatusState,
  statusReducer,
  setSubscribed,
  initialSubscribedState,
  subscribedReducer,
} from 'shared/state/summaryCard';
import { errorCodes } from 'shared/constants/errorCodes';
import { ratingTypes } from 'shared/constants/ratingTypes';

export const IssuerDashboardHeader = ({ entity, entityId }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [documents, setDocuments] = useState([]);
  const entityShouldRefresh = useSelector(getEntityShouldRefresh);
  const headSquareClasses = useHeadSquareStyles();
  const [subscribedState, subscribedDispatch] = useReducer(
    subscribedReducer,
    initialSubscribedState,
  );
  const [loadingState, loadingDispatch] = useReducer(isLoadingReducer, initialLoadingState);
  const [dataState, dataDispatch] = useReducer(dataReducer, initialDataState);
  const [statusState, statusDispatch] = useReducer(statusReducer, initialStatusState);

  useEffect(() => {
    fetchEntityDocuments({ entityId }).then(({ data }) => setDocuments(documentsParser(data)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const { name: company, methodologies } = entity;
  const { viewDocumentsModal, toggleModal, setDefaultExpand } = useViewDocuments({
    entityId,
    companyModule: ratingTypes.CREDIT_RATING,
    company,
    initialMethodologies: methodologies,
    initialDocuments: documents,
  });

  const fetchCreditRating = () => {
    loadingDispatch(setIsLoading(actions.setCreditRating, true));
    fetchCreditRatingSummaryTab(entityId)
      .then(data => {
        dataDispatch(setData(actions.setCreditRating, data));
        loadingDispatch(setIsLoading(actions.setCreditRating, false));
        statusDispatch(setStatus(actions.setCreditRating, 'wasRequested', true));
        statusDispatch(setStatus(actions.setCreditRating, 'isAvailable', true));
        subscribedDispatch(setSubscribed(actions.setCreditRating, 'isSubscribed', true));
      })
      .catch(({ status, message, error }) => {
        const isSubscribed = Number(error) !== errorCodes.creditRating.isSubscribed;

        switch (status) {
          case creditRatingStatuses.notFound:
            subscribedDispatch(setSubscribed(actions.setCreditRating, 'isSubscribed', false));
            statusDispatch(setStatus(actions.setCreditRating, 'isAvailable', false));
            break;
          case creditRatingStatuses.forbidden:
            subscribedDispatch(
              setSubscribed(actions.setCreditRating, 'isSubscribed', isSubscribed),
            );
            statusDispatch(setStatus(actions.setCreditRating, 'isAvailable', true));
            statusDispatch(setStatus(actions.setCreditRating, 'wasRequested', false));
            break;
          default:
            throw new Error(message);
        }

        loadingDispatch(setIsLoading(actions.setCreditRating, false));
      });
  };

  useEffect(() => {
    fetchCreditRating();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    if (entityShouldRefresh) {
      fetchCreditRating();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityShouldRefresh]);

  const statusIncludesNegativeValue = type => Object.values(statusState[type]).includes(false);
  const subscribedIncludesNegativeValue = type =>
    Object.values(subscribedState[type]).includes(false);

  const creditRatingData =
    !loadingState.creditRating &&
    dataState.creditRating.data &&
    !dataState.creditRating.data.some(item => !item.latestRating)
      ? summaryCardMapCreditRatingParser(dataState.creditRating.data)[0]
      : {};
  const showCreditRatingSummaryCard =
    loadingState.creditRating ||
    (!statusIncludesNegativeValue('creditRating') &&
      !subscribedIncludesNegativeValue('creditRating'));

  if (!showCreditRatingSummaryCard) {
    return (
      <Box mb={6}>
        <Loader center />
      </Box>
    );
  }

  return <>
    <Box mb={3}>
      <Typography variant="h4">
        Issuer Dashboard <strong>{entity.name}</strong>
      </Typography>
    </Box>
    <Box mb={2}>
      <SummaryCard
        headSquare={
          !loadingState.creditRating && (
            <ScoreWidgetMainColumn
              className={headSquareClasses.scoreWidgetMainColumn}
              value={creditRatingData.score || t('N/A')}
              label={creditRatingData.scoreTitle}
              footer={`${t('Outlook')}: ${creditRatingData.outlook || t('N/A')}`}
              footerColor={getScoreWidgetFooterColor(creditRatingData.outlook)}
            />
          )
        }
        main={
          <Grid container className={classes.businessCards} justifyContent="space-around">
            {!loadingState.creditRating &&
              creditRatingData.businessCards.map(businessCard => (
                <Grid item key={businessCard.name}>
                  <Title className={classes.titleMarginBottom}>{t(businessCard.title)}</Title>
                  <SummaryCardBusinessCard
                    id={businessCard.id}
                    name={businessCard.name}
                    position={businessCard.position}
                    industry={businessCard.industry}
                  />
                </Grid>
              ))}
          </Grid>
        }
        history={
          <SummaryCardLineChart
            ratingScale={creditRatingData.ratingScale}
            historyType={portfolioTypes.CREDIT_RATING}
            history={creditRatingData.history}
            latestChange={creditRatingData.latestChange}
          />
        }
        className={classes.summaryCard}
        isLoading={loadingState.creditRating}
        data={creditRatingData}
        documentHandling={{
          toggleModal,
          defaultDocument: setDefaultExpand,
        }}
      />
    </Box>
    {viewDocumentsModal}
  </>;
};

IssuerDashboardHeader.defaultProps = {
  entity: null,
};

IssuerDashboardHeader.propTypes = {
  entity: PropTypes.any,
  entityId: PropTypes.string.isRequired,
};
